import { useLayoutEffect } from 'react'
import { Switch, Redirect } from 'react-router'

import { languageSelector } from 'i18n/selectors'
import { Route, withRouter } from 'i18n/utils'
import { fixedRoutesSelector, customRoutesSelector, landingPageRouteSelector } from 'wordpress/selectors'
import { ContentPage } from 'content'

import Header from 'header'
import Footer from 'footer'
import { HomeView } from 'home'
import { MapView } from 'map'
import { DashboardContainerView } from 'dashboard'
import { NewsContainer } from 'news'
import { CarParkView } from 'car-parks'
import { POIView } from 'place-of-interest'
import { BookingView } from 'booking'
import { BasketView } from 'basket'
import { ConfirmationView } from 'confirmation'
import { OnStreetView } from 'on-street'
import { ActivationView } from 'activation'
import { ExtendView } from 'extending'
import { PurchaseFlow } from 'purchase-flow'
import { AirportsView } from 'airports'
import { ManageGuestBookingPage } from 'manage-guest-user-booking'

import { CookiePolicyPage } from 'cookies'

import { compose } from 'recompose'
import NotFound from 'not-found/NotFound'
import Button from '@components/Button'
import scrollTop from 'shared/scroll-top'
import featureToggles from 'shared/featureToggles'
import PayViolation from 'pay-violation/components'
import { NPSView } from 'nps-view'
import PixPayment from 'booking/components/PixPayment/PixPayment'
import Reminders from 'dashboard-account/reminders'
import { CUSTOM_ROUTES_WO_GRS } from 'core/constants'

const Routes = ({ fixedRoutes, customRoutes, landingPageRoute, language, location }) => {
    useLayoutEffect(() => {
        scrollTop()
    }, [location])

    return (
        <>
            <Header />
            <Reminders />
            <Switch>
                {/* Redirects */}
                {featureToggles.bookingRedirect && (
                    <Redirect
                        from={`/:language/booking/:grsId/subscriptions`}
                        to={`/:language/subscription/:grsId`}
                        replace
                    />
                )}
                {landingPageRoute && (
                    <Route
                        exact
                        path={'/:language'}
                        render={() => (
                            <Redirect
                                to={`/${language}/${landingPageRoute.slug}`}
                                replace
                                key={landingPageRoute.slug}
                            />
                        )}
                    />
                )}

                {/* Routes */}
                {Route({ path: '/', exact: true, component: HomeView })}
                {Route({ path: '/map', component: MapView })}
                {Route({ path: '/booking/:grsId/:parkingFlowUrlParameter?', exact: true, component: BookingView })}
                {Route({ path: '/booking/:grsId/edit/:reservationId?', exact: true, component: BookingView })}
                {Route({ path: '/basket', component: BasketView })}
                {Route({ path: '/pix', component: PixPayment })}
                {Route({ path: '/confirmation', component: ConfirmationView })}
                {Route({ path: '/dashboard', component: DashboardContainerView })}
                {Route({ path: '/extending', component: ExtendView })}
                {Route({ path: '/news', component: NewsContainer })}
                {Route({ path: '/payment-notice', component: PayViolation })}
                {Route({ path: '/car-park/:locationId', component: CarParkView })}
                {Route({ path: '/place-of-interest/:placeOfInterestId', component: POIView })}
                {Route({ path: '/on-street/:slug?/:pageId?', component: OnStreetView })}
                {Route({ path: '/activation', component: ActivationView })}
                {Route({ path: '/airports', component: AirportsView })}
                {Route({ path: '/nps/:type?/:surveyName?', component: NPSView })}
                {Route({ path: '/waitinglist/:waitingListId', component: PurchaseFlow })}
                {Route({ path: '/manage-my-booking/:details?', component: ManageGuestBookingPage })}

                {featureToggles.newSubscriptionFlow &&
                    Route({
                        path: '/subscription/:locationCode',
                        component: PurchaseFlow,
                    })}
                {fixedRoutes.map(({ id, slug }) =>
                    // Scanticket doesn't have locationCode in needed format, so we need to ignore locationCode for this route
                    CUSTOM_ROUTES_WO_GRS.includes(slug)
                        ? Route({
                              path: `${slug}`,
                              render: () => <ContentPage id={id[language]} path={location.pathname} />,
                              key: slug,
                          })
                        : Route({
                              path: `${slug}/:locationCode?`,
                              render: () => <ContentPage id={id[language]} path={location.pathname} />,
                              key: slug,
                          })
                )}
                {customRoutes
                    .filter(route => route.language === language)
                    .map(route => [
                        Route({
                            path: route.slug,
                            render: () => <ContentPage id={route.id} slug={route.slug} />,
                            key: route.slug,
                        }),
                        ...Object.values(route.translations).map(slug => (
                            <Redirect
                                from={`/:language/${slug}`}
                                to={`/${language}/${route.slug}`}
                                replace
                                key={route.slug}
                            />
                        )),
                    ])}
                {
                    // Cookie policy url must be added to footer from CMS,
                    // as the all footer links and pages are handled by CMS
                    // It uses React component, because switching Cookies on
                    // and off is handled by React
                    featureToggles.showCookiePolicy && Route({ path: '/cookie-policy', component: CookiePolicyPage })
                }
                {Route({
                    render: () => {
                        if (window.location.pathname !== `/${language}/404`) {
                            window.location.href = `/${language}/404`
                        }
                        return (
                            <NotFound status="404" message={<T>page.not.found</T>}>
                                <Button size="large" className="mt-5" href="/">
                                    <T>page.not.found.btn</T>
                                </Button>
                            </NotFound>
                        )
                    },
                })}
            </Switch>
            <Footer />
        </>
    )
}

export default compose(
    withRouter,
    connect(
        createStructuredSelector({
            fixedRoutes: fixedRoutesSelector,
            customRoutes: customRoutesSelector,
            language: languageSelector,
            landingPageRoute: landingPageRouteSelector,
        })
    )
)(Routes)
